import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, IconButton, Stack, Chip, Button, CircularProgress, Divider
} from '@mui/material';
import { Close, History, ShoppingCart, Person, Refresh, DeleteSweep } from '@mui/icons-material';
import { useListOrdersQuery, useDiscardHeldOrdersMutation } from '../../services/orderApi';
import { useToast } from '../common/ToastProvider';
import DiscardHeldBillsDialog from './DiscardHeldBillsDialog';

const HoldOrdersDrawer = ({ open, onClose, onResume, terminalId }) => {
  // Scoped to this terminal — a store can have several, and "Discard All"
  // below only ever clears the current one's bills, so the list shown here
  // must match exactly what that action touches.
  const { data, isFetching, refetch } = useListOrdersQuery(
    { status: 'on_hold', per_page: 50, terminal_id: terminalId },
    { skip: !open }
  );

  const orders = data?.data?.orders || (Array.isArray(data?.data) ? data.data : []);

  const showToast = useToast();
  const [discardHeldOrders, { isLoading: isDiscarding }] = useDiscardHeldOrdersMutation();
  const [isDiscardPromptOpen, setIsDiscardPromptOpen] = useState(false);

  const terminalHeldCount = orders.length;

  const handleDiscardAll = async () => {
    try {
      await discardHeldOrders().unwrap();
      showToast('Held bills discarded', 'success');
      setIsDiscardPromptOpen(false);
      refetch();
    } catch (err) {
      showToast(err?.data?.error || 'Failed to discard held bills. Please try again.', 'error');
    }
  };

  const [focusIndex, setFocusIndex] = useState(-1);

  // Roving focus over held-order cards: ArrowUp/Down move, Enter resumes the focused order
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (orders.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex(i => (i + 1 >= orders.length ? 0 : i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex(i => (i <= 0 ? orders.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Enter' && focusIndex >= 0 && orders[focusIndex]) {
        e.preventDefault();
        onResume(orders[focusIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, orders, focusIndex, onResume]);

  const handleClose = () => {
    setFocusIndex(-1);
    onClose();
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch (_) {
      return '';
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100vw', sm: 420 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' }
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2,
        borderBottom: '1px solid #E2E8F0',
        display: 'flex', alignItems: 'center', gap: 1.5,
        flexShrink: 0,
      }}>
        <History sx={{ color: '#6366F1', fontSize: 22 }} />
        <Typography fontWeight={800} fontSize="1rem" sx={{ flexGrow: 1 }}>On Hold Orders</Typography>
        {orders.length > 0 && (
          <Chip
            size="small" label={orders.length}
            sx={{ fontWeight: 800, bgcolor: '#EEF2FF', color: '#4F46E5', height: 22, fontSize: '0.72rem' }}
          />
        )}
        <IconButton size="small" onClick={refetch} disabled={isFetching} sx={{ color: '#64748B' }}>
          <Refresh fontSize="small" />
        </IconButton>
        {terminalHeldCount > 0 && (
          <Button
            size="small"
            startIcon={<DeleteSweep sx={{ fontSize: '16px !important' }} />}
            onClick={() => setIsDiscardPromptOpen(true)}
            sx={{
              fontWeight: 700, textTransform: 'none', fontSize: '0.72rem',
              color: '#EF4444', px: 1, minWidth: 0,
              '&:hover': { bgcolor: '#FEF2F2' },
            }}
          >
            Discard All
          </Button>
        )}
        <IconButton size="small" onClick={handleClose} sx={{ color: '#64748B' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DiscardHeldBillsDialog
        open={isDiscardPromptOpen}
        heldCount={terminalHeldCount}
        loading={isDiscarding}
        onCancel={() => setIsDiscardPromptOpen(false)}
        onDiscardAll={handleDiscardAll}
      />

      {/* Body */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: '#CBD5E1' }}>
            <History sx={{ fontSize: 52, mb: 1.5, opacity: 0.5 }} />
            <Typography fontWeight={700} fontSize="0.95rem" color="#94A3B8" sx={{ mb: 0.5 }}>
              No orders on hold
            </Typography>
            <Typography fontSize="0.78rem" color="#CBD5E1">
              Use "Hold Bill" to save a bill and come back to it later
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {orders.map((order, i) => (
              <Box key={order.id} sx={{
                border: '1px solid #E2E8F0', borderRadius: '10px',
                p: 2, bgcolor: i === focusIndex ? '#EEF2FF' : '#FFF',
                borderLeft: i === focusIndex ? '3px solid #4F46E5' : '1px solid #E2E8F0',
                transition: 'border-color 0.15s, background 0.15s',
                '&:hover': { borderColor: '#C7D2FE', bgcolor: '#FAFAFE' },
              }}>
                {i > 0 && <Divider sx={{ mb: 1.5, display: 'none' }} />}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                  {/* Left: order info */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                      <Typography fontWeight={800} fontSize="0.92rem" color="#1E293B">
                        {order.reference_number || order.order_number}
                      </Typography>
                      <Chip
                        size="small" label="On Hold"
                        sx={{
                          height: 18, fontSize: '0.6rem', fontWeight: 700,
                          bgcolor: '#FFF7ED', color: '#D97706',
                          border: '1px solid #FED7AA',
                        }}
                      />
                    </Stack>

                    {order.customer_name && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Person sx={{ fontSize: 13, color: '#94A3B8' }} />
                        <Typography fontSize="0.75rem" color="#64748B" fontWeight={600}>
                          {order.customer_name}
                        </Typography>
                      </Stack>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                      <ShoppingCart sx={{ fontSize: 13, color: '#94A3B8' }} />
                      <Typography fontSize="0.72rem" color="#64748B">
                        {order.items_count ?? order.items?.length ?? 0} item{(order.items_count ?? order.items?.length ?? 0) !== 1 ? 's' : ''}
                      </Typography>
                      <Typography fontSize="0.72rem" color="#CBD5E1">•</Typography>
                      <Typography fontSize="0.72rem" color="#94A3B8">
                        {formatTime(order.created_at)}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Right: total + resume */}
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography fontWeight={900} fontSize="1.05rem" color="#4F46E5">
                      ₹{parseFloat(order.grand_total || 0).toFixed(2)}
                    </Typography>
                    <Button
                      size="small" variant="contained" disableElevation
                      onClick={() => onResume(order)}
                      sx={{
                        mt: 0.75, bgcolor: '#4F46E5', fontWeight: 700,
                        textTransform: 'none', borderRadius: '6px',
                        fontSize: '0.72rem', height: 28, px: 1.5,
                        '&:hover': { bgcolor: '#4338CA' },
                      }}
                    >
                      Resume
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

export default HoldOrdersDrawer;
